/**
 * The dashboard.
 *
 * Deliberately readable with the vault locked. It shows schedule and readiness
 * — which is exactly the data the server already has — so the app is useful the
 * instant it opens, and unlocking is something you do to run a drill rather
 * than something you do to see anything at all.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'motion/react';
import { readinessBand, DAY_MS } from '@recovery/shared';
import { BandPill, ReadinessRing } from '../components/Readiness';
import { LinkButton } from '../components/Button';
import { springStandard } from '../design/motion';
import type { AnchorRow, DrillRow } from '../lib/session';
import * as vault from '../vault/vault';
import type { UnlockedAnchor } from '../vault/types';
import { PersistenceBanner } from './PersistenceBanner';
import { useLanguage } from '../i18n/context';
import type { Translations } from '../i18n/types';

function relativeDue(t: Translations, at: number, now: number): string {
  const days = Math.round((at - now) / DAY_MS);
  if (days < -1) return t.common.daysOverdue(Math.abs(days));
  if (days === -1) return t.common.dueYesterday;
  if (days === 0) return t.common.dueToday;
  if (days === 1) return t.common.dueTomorrow;
  if (days < 30) return t.common.dueInDays(days);
  return t.common.dueInMonths(Math.round(days / 30));
}

export function Dashboard({
  anchors,
  drills,
  unlocked,
}: {
  anchors: AnchorRow[];
  drills: DrillRow[];
  unlocked: boolean;
}) {
  const { t } = useLanguage();
  const [names, setNames] = useState<Map<string, string>>(new Map());
  const now = Date.now();

  useEffect(() => {
    if (!unlocked) {
      setNames(new Map());
      return;
    }
    let cancelled = false;
    void vault.listAnchors().then((entries: UnlockedAnchor[]) => {
      if (cancelled) return;
      setNames(new Map(entries.map((e) => [e.id, e.payload.displayName])));
    });
    return () => {
      cancelled = true;
    };
  }, [unlocked, anchors.length]);

  const dueNow = drills.filter((d) => d.scheduledFor <= now);

  return (
    <div className="mx-auto w-full max-w-lg px-5 pb-28">
      <header className="pt-[max(1.5rem,env(safe-area-inset-top))] pb-6">
        <h1 className="text-display text-ink-7">{t.dashboard.title}</h1>
        <p className="text-body mt-1 text-ink-4">
          {anchors.length === 0
            ? t.dashboard.nothingTracked
            : t.dashboard.trackedCount(anchors.length)}
        </p>
      </header>

      <PersistenceBanner />

      {dueNow.length > 0 && (
        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={springStandard}
          className="material-card mb-6 rounded-(--radius-card) p-4"
        >
          <h2 className="text-title text-ink-7">
            {dueNow.length === 1
              ? t.dashboard.drillDueTitleSingle
              : t.dashboard.drillDueTitleMultiple(dueNow.length)}
          </h2>
          <p className="text-body mt-1 mb-4 text-ink-5">
            {t.dashboard.drillDueDesc}
          </p>
          <LinkButton size="lg" className="w-full" to={`/drill/${dueNow[0]!.id}`}>
            {t.dashboard.startDrillCta}
          </LinkButton>
        </motion.section>
      )}

      {anchors.length === 0 ? (
        <EmptyState />
      ) : (
        <ul className="flex flex-col gap-3">
          {anchors.map((anchor, index) => (
            <motion.li
              key={anchor.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ ...springStandard, delay: Math.min(index * 0.04, 0.2) }}
            >
              <Link
                to={`/anchors/${anchor.id}`}
                className="material-card flex items-center gap-4 rounded-(--radius-card) p-4 transition-transform duration-100 ease-out active:scale-[0.99] motion-reduce:active:scale-100"
              >
                <ReadinessRing score={anchor.readinessScore} />
                <div className="min-w-0 flex-1">
                  <p className="text-title truncate text-ink-7">
                    {names.get(anchor.id) ?? (unlocked ? t.dashboard.unreadable : t.dashboard.locked)}
                  </p>
                  <p className="text-label mt-0.5 text-ink-4">
                    {relativeDue(t, anchor.nextDueAt, now)}
                  </p>
                  <div className="mt-2">
                    <BandPill score={anchor.readinessScore} />
                  </div>
                </div>
              </Link>
            </motion.li>
          ))}
        </ul>
      )}

      {anchors.length > 0 && (
        <p className="text-label mt-6 text-ink-4">
          {t.readinessBands.meanings[readinessBand(Math.min(...anchors.map((a) => a.readinessScore)))]}
        </p>
      )}
    </div>
  );
}

function EmptyState() {
  const { t } = useLanguage();
  return (
    <div className="material-card rounded-(--radius-card) p-6 text-center">
      <h2 className="text-title text-ink-7">{t.dashboard.emptyTitle}</h2>
      <p className="text-body mt-2 mb-5 text-ink-5">
        {t.dashboard.emptyBody}
      </p>
      <LinkButton size="lg" className="w-full" to="/anchors/new">
        {t.dashboard.emptyCta}
      </LinkButton>
    </div>
  );
}
